import { P500UIBaseModal, IModalsService, IMODALS_SERVICE, INotificationsService } from '@platform500services/p500-ui-kit';
import { AfterContentChecked, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, UntypedFormControl, FormControl } from '@angular/forms';
import { INOTIFICATIONS_SERVICE } from '@platform500services/p500-ui-kit';
import { forkJoin } from 'rxjs';
import { PresetsDrillDownService } from '../../services/presets-drill-down.service';
import { addCurrencyCondition, getComponent, getSignsData } from '../../../../../core/helpers/preset-condition.helper';
import { IDataService, IParsedDataResponse } from '../../../../../core/services/data/data-service.interface';
import { PageServiceInterface } from '../../../../../core/services/page/page-service.interface';
import { RbacServiceInterface } from '../../../../../core/services/rbac/rbac-service.interface';
import { ConditionTypes } from '../../../../../core/helpers/preset-condition.interface';
import { CustomValidators } from '../../../../../core/validators/validators';
import { debounceTime } from 'rxjs/operators';
import { DestroySubscribers } from '../../../../../core/decorators/destroy-subscribers.decorator';
import { SettingsResponseInterface } from '../../services/responses/preset.response';
import { transformRange } from '../../../../../core/components/date-interval/date-interval.model';
import { Router } from '@angular/router';
import { IRelations, relationMap } from 'src/app/modules/reporting/drilldown-reports/components/drilldown-report-filter/relations.helper';
import { RelationModalComponent } from '../../../../reporting/drilldown-reports/modals/relation-modal/relation-modal.component';
import { CONDITIONS_TRANSLATIONS } from '../../../../../core/services/data/data-constants-translations';
import { DRILLDOWN_GROUP_LABEL_TRANSLATIONS } from '../../../../reporting/drilldown2/config/translations';

@Component({
  selector: 'gtd-drill-down-modal',
  templateUrl: './drill-down.modal.html',
})
@DestroySubscribers()
export class DrillDownModal extends P500UIBaseModal implements OnInit, AfterContentChecked {
  @Input() public id: number;
  public settings?: any;
  public isLoading = true;
  public name: string;
  public title: string;
  public hash: string;
  public groups: SettingsResponseInterface['groups'];
  public dateIntervals = Object.entries(transformRange).reduce((acc, [key, value]) => ((acc[key] = value.title), acc), {});
  public columnsData: Record<string, string>[];
  public showCurrencySelect = false;

  public presetHelpers = {
    getValueComponentByType: (formControl: UntypedFormControl, key: string, type: ConditionTypes = '', label: string = '') => {
      return getComponent(formControl, key, type, label, this.dataService.getData());
    },
    getSignsData,
  };

  public form = this.fb.group({
    presetName: ['', [Validators.required, Validators.maxLength(25)]],
    presetType: [0, Validators.required],
    is_flat: [false, Validators.required],
    add_relation_columns: [true, Validators.required],
    show_in_menu: [false, Validators.required],
    default_range: ['today', Validators.required],
    filters: this.fb.group({
      groups: new UntypedFormControl([], [Validators.required]),
      columns: new UntypedFormControl([], Validators.required),
    }),
    currency: new FormControl(null),
  });
  public relationMap = relationMap;
  protected readonly CONDITIONS_TRANSLATIONS = CONDITIONS_TRANSLATIONS;
  readonly groupLabelTranslations = DRILLDOWN_GROUP_LABEL_TRANSLATIONS;
  readonly globalData: IParsedDataResponse;
  public filterLocalization = new Map([
    ['Conditions', $localize`:@@common.conditions:Conditions`],
    ['Switchers', $localize`:@@common.switchers:Switchers`],
  ]);

  @HostBinding('class') get className() {
    return `c-modal c-modal--d -primary -preset ${this.isLoading && 'c-modal--is--loading'}`;
  }

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    @Inject(INOTIFICATIONS_SERVICE) private notificationsService: INotificationsService,
    @Inject('PresetsDrillDownServiceInterface') protected presetsDrillDownService: PresetsDrillDownService,
    @Inject('RbacServiceInterface') public rbacService: RbacServiceInterface,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('PageServiceInterface') protected pageServiceInterface: PageServiceInterface,
    private fb: UntypedFormBuilder,
    public cd: ChangeDetectorRef,
    public router: Router
  ) {
    super(modalsService, router);
    this.globalData = this.dataService.getData();
  }

  ngOnInit(): void {
    if (this.id) {
      this.editModalData(this.id);
    } else {
      this.defaultModalData();
    }
    this.subscribers.conditionsValid = this.filters.statusChanges.pipe(debounceTime(5)).subscribe(() => {
      if (this.filters.controls.conditions.errors?.duplicateConditions) {
        this.notificationsService.add(
          {
            type: 'error',
            title: $localize`:@@settings.identical_conditions_use_multiselect:Identical conditions. Use multiselect`,
          },
          true
        );
      }
    });
    if (this.globalData.available_offer_currencies?.length > 1) {
      this.form.controls.currency.setValue(this.globalData.default_currency.currency);
    }
  }

  openRelationModal(group: string): void {
    const data = this.relationMap.get(group);
    this.modalsService.open(RelationModalComponent, {
      data: data.relationsData,
      value: data.relations,
      cb: (relations: IRelations) => {
        if (Object.keys(relations).length === 0) {
          this.relationMap.clearAllRelations();
          return;
        }
        this.relationMap.setRelationsByGroup(group, relations);
      },
    });
  }

  clearRelations(key: string): void {
    this.relationMap.clearRelations(key);
    this.form.updateValueAndValidity();
  }

  get hasRelations(): boolean {
    return this.filters.controls.groups.value?.some((item) => this.relationMap.has(item.key));
  }

  get filters(): UntypedFormGroup {
    return this.form.get('filters') as UntypedFormGroup;
  }

  get presetName(): UntypedFormControl {
    return this.form.get('presetName') as UntypedFormControl;
  }

  addCondition(key: string): void {
    (this.filters.get(key) as UntypedFormArray).setValidators([CustomValidators.duplicateConditions]);
    (this.filters.get(key) as UntypedFormArray).push(this.fb.control({ value: null }));
  }

  removeCondition(key: string, index: number): void {
    (this.filters.get(key) as UntypedFormArray).removeAt(index);
  }

  submit(): void {
    this.isLoading = true;
    const filters = this.filters.value;
    filters.conditions = addCurrencyCondition(filters.conditions, this.form.value.currency);
    const preset = {
      ...this.form.value,
      name: this.presetName.value,
      filters: {
        ...filters,
        relations: this.relationMap.getRelationsByGroups(filters.groups.map(({ key }) => key)),
      },
      id: this.id,
      hash: this.hash,
    };
    this.presetsDrillDownService.process(preset).subscribe(
      () => {
        this.close();
        this.notificationsService.add(
          {
            type: 'success',
            title: $localize`:@@common.success:Success`,
          },
          true
        );
        this.pageServiceInterface.refresh();
      },
      () => (this.isLoading = false)
    );
  }

  private setColumns(settings: SettingsResponseInterface): void {
    this.columnsData = settings.filters.columns;
  }

  private defaultModalData(): void {
    this.title = $localize`:@@common.create_preset:Create Preset`;
    this.presetsDrillDownService.getDefaultSettings().subscribe((settings) => {
      this.relationMap.init(settings.groupRelations);
      this.settings = settings;
      this.setColumns(settings);
      this.groups = settings.groups;
      this.groups.forEach((group) => {
        group.label = this.groupLabelTranslations[group.label] ?? group.label;
      });
      for (const [key, value] of Object.entries(settings.filters)) {
        switch (value.type) {
          case 'conditions':
            this.filters.addControl(key, this.fb.array([]));
            break;
          case 'switchers':
            this.filters.addControl(key, this.fb.control([]));
            break;
        }
      }
      this.isLoading = false;
    });
  }

  private editModalData(id: number): void {
    this.title = $localize`:@@settings.edit_preset:Edit Preset`;
    forkJoin({
      settingsById: this.presetsDrillDownService.getSettingsById(id),
      defaultSettings: this.presetsDrillDownService.getDefaultSettings(),
    }).subscribe(({ settingsById, defaultSettings }) => {
      const values = settingsById.reduce((acc, v) => ((acc[v.name] = v.value), acc), {}) as Record<string, any>;
      this.form.patchValue(values);
      this.name = values.name;
      this.hash = values.hash;
      this.settings = values.content;
      this.relationMap.init(defaultSettings.groupRelations);
      this.relationMap.setAllRelations(values.content?.filters?.relations || {});
      this.settings.filters.conditions.items = defaultSettings.filters.conditions.items;
      this.groups = defaultSettings.groups;
      this.groups.forEach((group) => {
        group.label = this.groupLabelTranslations[group.label] ?? group.label;
      });
      this.presetName.patchValue(this.name);
      const groupsMap = defaultSettings.groups.reduce((acc, item) => acc.set(item.key, item), new Map());
      this.filters.get('groups').patchValue(this.settings.filters.groups.map((value) => groupsMap.get(value.key)).filter((value) => value));
      this.setColumns(defaultSettings);
      const columnsMap = this.columnsData.reduce((acc, item) => (acc.set(item.key, item), acc), new Map());
      this.filters.get('columns').patchValue(this.settings.filters.columns.map((item) => columnsMap.get(item.key)).filter((v) => v));
      for (const [key, value] of Object.entries(this.settings.filters || {})) {
        switch (key) {
          case 'conditions':
            this.settings.filters.conditions.label = 'Conditions';
            this.settings.filters.conditions.type = 'conditions';
            this.filters.addControl(key, this.fb.array([]));
            if (Array.isArray(value)) {
              value
                .filter((condition) => condition.key !== 'currency_code')
                .forEach((item) => {
                  (this.filters.get(key) as UntypedFormArray).push(this.fb.control(item, Validators.required));
                });
            }
            break;
          case 'switchers':
            this.settings.filters.conditions.label = 'Switchers';
            this.settings.filters.conditions.type = 'switchers';
            this.filters.addControl(key, this.fb.control([]));
            (this.filters.get(key) as UntypedFormControl).setValue(value);
            break;
        }
      }
      const currencyCondition = this.settings.filters.conditions.find((condition) => condition.key === 'currency_code');
      if (currencyCondition) {
        this.form.controls.currency.setValue(currencyCondition.value);
      }
      this.isLoading = false;
    });
  }

  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }
}
